/**
 * Cart drawer (mini-cart) controller.
 *
 * Self-contained; does not depend on pacific.js internals. Uses fetch + the
 * Shopify Section Rendering API (section id "cart-drawer") to keep contents in
 * sync, with all price formatting done server-side in Liquid.
 *
 * Behaviour (per approved plan):
 *   - Add to cart  -> intercept the product form submit, POST /cart/add.js,
 *                     update the header badge, then either open the drawer
 *                     (product pages) or show a toast (everywhere else).
 *                     Which one is decided in Liquid via data-auto-open.
 *   - Cart icon    -> opens the drawer instead of navigating to /cart.
 *   - Qty steppers -> POST /cart/change.js and swap the drawer contents.
 */
(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';
  var DEFAULT_TOAST_DURATION = 5000;
  var SCROLL_DISMISS_THRESHOLD = 10; // px of page scroll before the toast fades out early

  var routes = (window.Theme && window.Theme.routes) || {};
  var cartAddUrl = routes.cart_add_url || '/cart/add';
  var cartChangeUrl = routes.cart_change_url || '/cart/change';
  var cartUrl = routes.cart_url || '/cart';
  var rootUrl = routes.root_url || '/';

  var CartDrawer = {
    root: null,
    panel: null,
    contents: null,
    overlay: null,
    closeButton: null,
    trigger: null,
    previouslyFocused: null,
    loading: false,
    toast: null,
    toastTimer: null,
    toastDuration: DEFAULT_TOAST_DURATION,
    autoOpen: false,
    isCartPage: false,
    toastScrollHandler: null,
    toastScrollStart: 0,
    keydownHandler: null,
    scrollY: 0,

    init: function () {
      this.cacheRefs();
      if (!this.root) return;
      // Signals to CSS that the JS is live, so the inline add-to-cart success
      // message can be suppressed in favour of the toast. If the JS fails to
      // load this class is never added and Pacific's native message shows.
      document.documentElement.classList.add('cart-drawer-enabled');
      this.bindShell();
      this.bindTriggers();
      this.bindAddToCart();
      this.bindQuantityDelegation();
      this.buildToast();
    },

    cacheRefs: function () {
      this.root = document.querySelector('[data-cart-drawer]');
      if (!this.root) return;
      this.panel = this.root.querySelector('[data-cart-drawer-panel]');
      this.contents = this.root.querySelector('[data-cart-drawer-contents]');
      this.overlay = this.root.querySelector('[data-cart-drawer-overlay]');
      this.closeButton = this.root.querySelector('[data-cart-drawer-close]');
      var duration = parseInt(this.root.getAttribute('data-toast-duration'), 10);
      this.toastDuration = (!isNaN(duration) && duration > 0) ? duration : DEFAULT_TOAST_DURATION;
      this.autoOpen = this.root.getAttribute('data-auto-open') === 'true';
      this.isCartPage = this.root.getAttribute('data-cart-page') === 'true';
    },

    /* ---------- Shell: open / close / focus / scroll lock ---------- */

    bindShell: function () {
      var self = this;
      if (this.overlay) {
        this.overlay.addEventListener('click', function () { self.close(); });
      }
      if (this.closeButton) {
        this.closeButton.addEventListener('click', function () { self.close(); });
      }
    },

    open: function () {
      // The cart page already renders the whole cart, so a drawer over it is
      // redundant, and the two views drift apart as soon as either is edited.
      // Guarding here covers every caller, not just the header trigger.
      if (this.isCartPage) return;
      if (!this.root || this.root.classList.contains('is-open')) return;
      this.previouslyFocused = document.activeElement;
      this.lockScroll();
      this.root.classList.add('is-open');
      this.root.setAttribute('aria-hidden', 'false');
      this.trapFocus();
      // Move focus into the drawer.
      var target = this.closeButton || this.panel;
      if (target) target.focus();
    },

    close: function () {
      if (!this.root || !this.root.classList.contains('is-open')) return;
      this.root.classList.remove('is-open');
      this.root.setAttribute('aria-hidden', 'true');
      this.releaseFocus();
      this.unlockScroll();
      if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
        this.previouslyFocused.focus();
      }
    },

    lockScroll: function () {
      this.scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      document.body.style.top = '-' + this.scrollY + 'px';
      document.body.classList.add('cart-drawer-open');
    },

    unlockScroll: function () {
      document.body.classList.remove('cart-drawer-open');
      document.body.style.top = '';
      window.scrollTo(0, this.scrollY);
    },

    trapFocus: function () {
      var self = this;
      this.keydownHandler = function (e) {
        if (e.key === 'Escape' || e.keyCode === 27) {
          self.close();
          return;
        }
        if (e.key !== 'Tab' && e.keyCode !== 9) return;
        var focusable = self.getFocusable();
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', this.keydownHandler);
    },

    releaseFocus: function () {
      if (this.keydownHandler) {
        document.removeEventListener('keydown', this.keydownHandler);
        this.keydownHandler = null;
      }
    },

    getFocusable: function () {
      if (!this.panel) return [];
      var nodes = this.panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      return Array.prototype.filter.call(nodes, function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
    },

    /* ---------- Triggers: header cart icon ---------- */

    bindTriggers: function () {
      var self = this;
      // On the cart page leave the header link native: intercepting it would
      // produce a click that does nothing, since open() is a no-op there.
      if (this.isCartPage) return;
      var links = document.querySelectorAll('.header-tools-cart, [data-cart-drawer-toggle]');
      Array.prototype.forEach.call(links, function (link) {
        link.addEventListener('click', function (e) {
          e.preventDefault();
          self.open();
        });
      });
    },

    /* ---------- Add to cart: capture-phase interception ---------- */

    bindAddToCart: function () {
      var self = this;
      // Capture phase runs before pacific.js's form-level (bubble) handler, so
      // stopImmediatePropagation cleanly replaces its add-to-cart behaviour.
      document.addEventListener('submit', function (e) {
        var form = e.target;
        if (!form || !form.matches || !form.matches('[data-product-form]')) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        self.addToCart(form);
      }, true);
    },

    addToCart: function (form) {
      var self = this;
      var formData;
      try {
        formData = new FormData(form);
      } catch (err) {
        form.submit();
        return;
      }
      formData.append('sections', SECTION_ID);

      fetch(cartAddUrl + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
        .then(function (res) {
          return res.json().then(function (data) {
            return { ok: res.ok, data: data };
          });
        })
        .then(function (result) {
          if (!result.ok) {
            self.showToast(
              (result.data && result.data.description) || 'Could not add to cart.',
              true
            );
            return;
          }
          var data = result.data;
          var reveal = function () {
            if (self.autoOpen) {
              self.open();
              return;
            }
            var title = data.product_title || data.title || 'Item';
            self.showToast(title + ' added to cart');
          };
          if (data.sections && data.sections[SECTION_ID]) {
            self.applySection(data.sections[SECTION_ID]);
            reveal();
          } else {
            // Section render missing from the response: refresh first so the
            // drawer (or the toast's "View cart") reflects the new line item.
            self.refresh().then(reveal);
          }
        })
        .catch(function () {
          // Network / unexpected error: fall back to a native add so the
          // customer is never blocked from purchasing.
          form.submit();
        });
    },

    /* ---------- Quantity changes (event delegation) ---------- */

    bindQuantityDelegation: function () {
      var self = this;
      if (!this.contents) return;
      // Re-query contents each time because it is replaced on refresh; delegate
      // from the stable root instead.
      this.root.addEventListener('click', function (e) {
        var decrease = e.target.closest && e.target.closest('[data-qty-decrease]');
        var increase = e.target.closest && e.target.closest('[data-qty-increase]');
        var remove = e.target.closest && e.target.closest('[data-qty-remove]');
        if (!decrease && !increase && !remove) return;
        e.preventDefault();
        if (self.loading) return;

        var itemEl = e.target.closest('[data-cart-item]');
        if (!itemEl) return;
        var key = itemEl.getAttribute('data-line-item-key');
        var input = itemEl.querySelector('[data-qty-input]');
        var current = parseInt(input && input.value, 10);
        if (isNaN(current)) current = 1;

        var next = current;
        if (remove) next = 0;
        else if (increase) next = current + 1;
        else if (decrease) next = Math.max(current - 1, 0);

        self.changeQuantity(key, next);
      });

      this.root.addEventListener('change', function (e) {
        var input = e.target.closest && e.target.closest('[data-qty-input]');
        if (!input) return;
        if (self.loading) return;
        var itemEl = e.target.closest('[data-cart-item]');
        if (!itemEl) return;
        var key = itemEl.getAttribute('data-line-item-key');
        var next = parseInt(input.value, 10);
        if (isNaN(next) || next < 0) next = 0;
        self.changeQuantity(key, next);
      });
    },

    changeQuantity: function (key, quantity) {
      var self = this;
      if (!key) return;
      this.setLoading(true);

      fetch(cartChangeUrl + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: key, quantity: quantity, sections: [SECTION_ID] })
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          if (data.sections && data.sections[SECTION_ID]) {
            self.applySection(data.sections[SECTION_ID]);
          } else {
            self.refresh();
          }
        })
        .catch(function () {
          self.refresh();
        })
        .then(function () {
          self.setLoading(false);
        });
    },

    setLoading: function (state) {
      this.loading = state;
      if (this.root) this.root.classList.toggle('cart-drawer--loading', state);
    },

    /* ---------- Section refresh helpers ---------- */

    // Swap only the contents region + badge from a rendered section string,
    // leaving the shell (overlay/panel/header) and open state untouched.
    applySection: function (html) {
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var newContents = parsed.querySelector('[data-cart-drawer-contents]');
      var newRoot = parsed.querySelector('[data-cart-drawer]');

      if (newContents && this.contents) {
        this.contents.innerHTML = newContents.innerHTML;
      }
      if (newRoot) {
        var count = parseInt(newRoot.getAttribute('data-cart-drawer-count'), 10);
        if (!isNaN(count)) this.setBadge(count);
      }
    },

    // Pull the latest section from the server (used as a fallback).
    refresh: function () {
      var self = this;
      return fetch(rootUrl + '?section_id=' + SECTION_ID, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (res) { return res.text(); })
        .then(function (html) { self.applySection(html); })
        .catch(function () {});
    },

    setBadge: function (count) {
      if (this.root) this.root.setAttribute('data-cart-drawer-count', count);
      var links = document.querySelectorAll('.header-tools-cart');
      Array.prototype.forEach.call(links, function (link) {
        if (count > 0) link.classList.add('cart-has-content');
        else link.classList.remove('cart-has-content');
        var countEl = link.querySelector('.cart-item-count');
        if (countEl) countEl.textContent = count;
      });
    },

    /* ---------- Toast ---------- */

    buildToast: function () {
      var toast = document.createElement('div');
      toast.className = 'cart-drawer-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
      this.toast = toast;
    },

    // Success on non-product pages (product pages open the drawer instead),
    // and any failed add. The success variant carries a "View cart" action so
    // the drawer is still one tap away without interrupting browsing.
    showToast: function (message, isError) {
      var self = this;
      if (!this.toast) this.buildToast();
      this.toast.classList.toggle('cart-drawer-toast--error', !!isError);

      var text = document.createElement('span');
      text.className = 'cart-drawer-toast__text';
      text.textContent = message;

      this.toast.innerHTML = '';
      this.toast.appendChild(text);

      if (!isError) {
        var view = document.createElement('button');
        view.type = 'button';
        view.className = 'cart-drawer-toast__action';
        view.textContent = 'View cart';
        view.addEventListener('click', function () {
          self.hideToast();
          self.open();
        });
        this.toast.appendChild(view);
      }

      this.toast.classList.add('is-visible');
      this.clearToastTimers();
      this.toastTimer = setTimeout(function () { self.hideToast(); }, this.toastDuration);
      this.bindToastScrollDismiss();
    },

    // Fade the toast out early once the shopper scrolls: a scroll is a strong
    // signal they've moved on, so we don't make them wait out the full timer.
    bindToastScrollDismiss: function () {
      var self = this;
      this.toastScrollStart = window.pageYOffset || document.documentElement.scrollTop || 0;
      this.toastScrollHandler = function () {
        var y = window.pageYOffset || document.documentElement.scrollTop || 0;
        if (Math.abs(y - self.toastScrollStart) > SCROLL_DISMISS_THRESHOLD) self.hideToast();
      };
      window.addEventListener('scroll', this.toastScrollHandler, { passive: true });
    },

    clearToastTimers: function () {
      if (this.toastTimer) { clearTimeout(this.toastTimer); this.toastTimer = null; }
      if (this.toastScrollHandler) {
        window.removeEventListener('scroll', this.toastScrollHandler);
        this.toastScrollHandler = null;
      }
    },

    hideToast: function () {
      if (this.toast) this.toast.classList.remove('is-visible');
      this.clearToastTimers();
    }
  };

  function start() {
    CartDrawer.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Theme editor: re-cache refs if the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.detail && e.detail.sectionId && e.detail.sectionId.indexOf(SECTION_ID) === -1) return;
    CartDrawer.cacheRefs();
    CartDrawer.bindShell();
  });

  window.CartDrawer = CartDrawer;
})();
